import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTournamentResultsEnhanced } from "../../hooks/useTournamentResultsEnhanced";
import { useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";
import { formatBoardMoney } from "../../utils/homepageSeason";
import { formatPosition } from "../UI/DesignStandards";
import { shouldShowResultPrizes } from "../../utils/prizeSnapshot";
import {
  boardEvents,
  boardNameColumnLabel,
  overviewBoardRows,
  preferredBoardEventId,
} from "../../utility/overviewBoard";

const pillClass = (active) =>
  active
    ? "bg-brand-charcoal text-brand-cream"
    : "bg-white text-brand-charcoal border border-brand-charcoal/20";

const TournamentOverviewBoard = ({ tournamentId }) => {
  const tournamentSlug = useTournamentSlug();
  const { results, tournament, loading } = useTournamentResultsEnhanced(
    tournamentId,
    null,
    false
  );
  const [selectedEventId, setSelectedEventId] = useState(null);

  if (loading) {
    return (
      <section className="card-board p-6" aria-label="Board">
        <div className="animate-pulse">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="h-6 w-24 bg-gray-200 rounded"></div>
            <div className="h-8 w-32 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </section>
    );
  }

  const events = boardEvents(results);
  if (events.length === 0) return null;

  const defaultEventId = preferredBoardEventId(results, events);
  const eventId =
    selectedEventId && events.some((event) => event.id === selectedEventId)
      ? selectedEventId
      : defaultEventId;
  const selectedEvent = events.find((event) => event.id === eventId);
  const includePrizes = shouldShowResultPrizes({
    isResultsFinalized: Boolean(tournament?.isResultsFinalized),
  });
  const rows = overviewBoardRows(results, eventId, { includePrizes });
  if (rows.length === 0) return null;

  const showPrize = rows.some((row) => row.prize != null);
  const nameLabel = boardNameColumnLabel(selectedEvent);
  const resultsHref = tournamentPath(tournamentSlug || tournamentId, "/standings");

  return (
    <section className="card-board p-6" aria-label="Board">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="heading-display text-xl">Board</h2>
        <Link
          to={resultsHref}
          className="btn-brand text-sm whitespace-nowrap flex-shrink-0"
        >
          Full Standings
        </Link>
      </div>

      {events.length > 1 ? (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {events.map((event) => {
            const active = event.id === eventId;
            return (
              <button
                key={event.id}
                type="button"
                onClick={() => setSelectedEventId(event.id)}
                className={`inline-flex items-center min-h-11 px-3 py-1 rounded-lg text-sm font-display font-bold tracking-wide ${pillClass(
                  active
                )}`}
                aria-pressed={active}
              >
                {event.name}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full text-left font-score text-sm">
          <thead>
            <tr className="text-brand-muted text-[10px] uppercase tracking-wider">
              <th className="py-0.5 pr-2 font-medium w-10" scope="col">
                Place
              </th>
              <th className="py-0.5 pr-2 font-medium" scope="col">
                {nameLabel}
              </th>
              <th
                className={`py-0.5 font-medium text-right ${showPrize ? "pr-2" : ""}`}
                scope="col"
              >
                Score
              </th>
              {showPrize ? (
                <th className="py-0.5 font-medium text-right" scope="col">
                  Prize
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-brand-charcoal/10">
                <td className="py-1 pr-2 heading-display text-base tabular-nums whitespace-nowrap">
                  {row.position != null ? formatPosition(row.position) : ""}
                </td>
                <td className="py-1 pr-2 text-brand-charcoal truncate max-w-[12rem]">
                  {row.name}
                </td>
                <td
                  className={`py-1 text-right text-brand-charcoal tabular-nums ${
                    showPrize ? "pr-2" : ""
                  }`}
                >
                  {row.score}
                </td>
                {showPrize ? (
                  <td className="py-1 text-right text-brand-charcoal tabular-nums">
                    {row.prize != null ? formatBoardMoney(row.prize) : ""}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default TournamentOverviewBoard;
